import React from 'react'
import {
    Box,
    Modal,
    Paper,
    Stack,
    Typography,
    IconButton,
    Divider,
    Button,
    TextField,
    InputAdornment,
    Avatar,
    Switch,
    Collapse,
    Grid,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DateFieldWithIcon from './DatePicker'
import AddManager from './AddManager'
import {
    useGetProjectTrackerStaff,
    useGetProjectTrackerProjectById,
    useUpdateProjectTrackerProject,
    PROJECT_TRACKER_PROJECTS_QK,
    PROJECT_TRACKER_PROJECT_QK,
    useMyAccount,
} from 'services/api/task-management/trackerHooks'
import { useQueryClient } from 'react-query'
import { Tooltip } from '@mui/material'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { isHiddenProjectTrackerStaff } from '../helpers/hiddenStaff'

function MemberAvatar({ name, src, size = 26 }) {
    const [imgError, setImgError] = React.useState(false)
    const initial = (name || '?').trim()?.[0]?.toUpperCase?.() || '?'
    const useFallback = !src || imgError

    return (
        <Avatar
            src={useFallback ? undefined : src}
            alt={name || 'member'}
            imgProps={{
                referrerPolicy: 'no-referrer',
                onError: () => setImgError(true),
            }}
            sx={{
                width: size,
                height: size,
                bgcolor: useFallback ? '#F5C542' : 'transparent',
                color: 'black',
                fontWeight: 900,
                fontSize: Math.max(12, Math.floor(size * 0.45)),
            }}
        >
            {useFallback ? initial : null}
        </Avatar>
    )
}

function StepDots({ step }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 99,
                    bgcolor: step === 1 ? '#1a73e8' : 'rgba(0,0,0,0.2)',
                }}
            />
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 99,
                    bgcolor: step === 2 ? '#1a73e8' : 'rgba(0,0,0,0.2)',
                }}
            />
        </Stack>
    )
}

function newDeliverable(creator = null) {
    return {
        _tmpId: String(Date.now() + Math.random()),
        id: null,
        type: '',
        value: '',
        allowOverflow: false,
        touched: { type: false, value: false },
        isNew: true,
        createdBy: creator?.id ?? null,
        creator,
    }
}

function isValidDateDDMMYYYY(s) {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((s || '').trim())
    if (!m) return false

    const dd = Number(m[1])
    const mm = Number(m[2])
    const yyyy = Number(m[3])

    if (mm < 1 || mm > 12) return false
    if (dd < 1 || dd > 31) return false

    const d = new Date(yyyy, mm - 1, dd)
    return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd
}

function toISODateFromDDMMYYYY(ddmmyyyy) {
    if (!isValidDateDDMMYYYY(ddmmyyyy)) return null
    const [dd, mm, yyyy] = ddmmyyyy.split('/').map(Number)
    const pad = (n) => String(n).padStart(2, '0')
    return `${yyyy}-${pad(mm)}-${pad(dd)}`
}

function isoToDDMMYYYY(isoString) {
    if (!isoString) return ''
    const d = new Date(isoString)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

function toRelatedLinks(linkText) {
    const raw = (linkText || '').trim()
    if (!raw) return []
    return raw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
}

function countWords(text) {
    return (text || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean).length
}

function toLinksText(linksArr) {
    if (!Array.isArray(linksArr)) return ''
    return linksArr.filter(Boolean).join('\n')
}

function toDateOnly(isoString) {
    if (!isoString) return null
    return String(isoString).slice(0, 10)
}

function arraysEqual(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false
    return a.every((item, index) => item === b[index])
}

function buildDeliverablePayload(deliverable) {
    return {
        type: String(deliverable.type || '').trim(),
        value: Number(deliverable.value),
        overflow_allowed: Boolean(deliverable.allowOverflow),
    }
}

function getStaffAvatar(staff) {
    return staff?.staff_profile_image?.profile_image_url ?? staff?.profile_image_url ?? null
}

function toStaffDisplay(staff) {
    if (!staff) return null

    return {
        id: staff?.id ?? null,
        name: staff?.name ?? '-',
        avatarUrl: getStaffAvatar(staff),
    }
}

function canEditDeliverableRecord(deliverable, currentStaffId, isProjectMaster) {
    if (deliverable?.id == null) return true
    if (isProjectMaster) return true
    if (deliverable?.created_by == null && deliverable?.createdBy == null) return false

    const createdBy = deliverable?.created_by ?? deliverable?.createdBy
    return currentStaffId != null && String(createdBy) === String(currentStaffId)
}

function resolveProjectMasterStaff(project) {
    const projectMasterId = project?.created_by
    if (projectMasterId == null) return null

    return (
        (project?.members ?? []).find((m) => String(m?.staff_id) === String(projectMasterId))?.staff ??
        (project?.members ?? [])
            .map((m) => m?.staff ?? null)
            .find((staff) => String(staff?.id) === String(projectMasterId)) ??
        null
    )
}

function resolveDeliverableCreator(deliverable, projectMasterStaff) {
    if (deliverable?.created_by == null) {
        return toStaffDisplay(projectMasterStaff)
    }

    return toStaffDisplay(deliverable?.creator)
}

function toProjectManagerStaff(staff) {
    if (!staff) return null

    return {
        ...staff,
        profile_image_url: staff?.profile_image_url ?? getStaffAvatar(staff),
    }
}

function buildProjectPatchPayload(project, values, permissions = {}) {
    const { currentStaffId, isProjectMaster } = permissions
    const payload = {}

    const name = values.projectName.trim()
    const objective = values.objective?.trim() || null
    const background = values.background?.trim() || null
    const relatedLinks = toRelatedLinks(values.link)

    if (name !== (project?.name ?? '')) payload.name = name
    if (objective !== (project?.objective ?? null)) payload.objective = objective
    if (background !== (project?.background ?? null)) payload.background = background
    if (values.startISO !== toDateOnly(project?.start_date)) payload.start_date = values.startISO
    if (values.endISO !== toDateOnly(project?.deadline)) payload.deadline = values.endISO
    if (!arraysEqual(relatedLinks, project?.related_links ?? [])) payload.related_links = relatedLinks

    const members = project?.members ?? []

    if (isProjectMaster) {
        const originalManagerIds = members
            .filter((m) => String(m?.role || '').toLowerCase() === 'manager')
            .map((m) => Number(m?.staff?.id ?? m?.staff_id))
            .filter((id) => Number.isFinite(id))

        const currentManagerIds = (values.projectManagers || [])
            .map((m) => Number(m?.id))
            .filter((id) => Number.isFinite(id))

        const originalManagerSet = new Set(originalManagerIds)
        const currentManagerSet = new Set(currentManagerIds)

        const addManagerIds = currentManagerIds.filter((id) => !originalManagerSet.has(id))
        const removeManagerIds = originalManagerIds.filter((id) => !currentManagerSet.has(id))

        if (addManagerIds.length > 0) payload.add_manager_ids = addManagerIds
        if (removeManagerIds.length > 0) payload.remove_manager_ids = removeManagerIds
    }

    const originalTeamMembers = members.filter((m) => String(m?.role || '').toLowerCase() === 'team_member')
    const originalTeamByStaffId = new Map(
        originalTeamMembers
            .map((m) => [Number(m?.staff?.id ?? m?.staff_id), m])
            .filter(([staffId]) => Number.isFinite(staffId))
    )

    const currentTeamMembers = values.selected || []
    const currentTeamIds = currentTeamMembers
        .map((m) => Number(m?.id))
        .filter((id) => Number.isFinite(id))
    const currentTeamSet = new Set(currentTeamIds)

    const addTeamMembers = []
    const updateMemberDeliverables = []

    currentTeamMembers.forEach((member) => {
        const staffId = Number(member?.id)
        if (!Number.isFinite(staffId)) return

        const deliverables = member?.deliverables || []
        const originalMember = originalTeamByStaffId.get(staffId)

        if (!originalMember) {
            addTeamMembers.push({
                staff_id: staffId,
                deliverables: deliverables
                    .filter((d) => canEditDeliverableRecord(d, currentStaffId, isProjectMaster))
                    .map(buildDeliverablePayload),
            })
            return
        }

        const originalDeliverables = originalMember?.deliverables || []
        const originalDeliverablesById = new Map(
            originalDeliverables
                .map((deliverable) => [Number(deliverable?.id), deliverable])
                .filter(([deliverableId]) => Number.isFinite(deliverableId))
        )

        const currentDeliverableIds = deliverables
            .map((deliverable) => Number(deliverable?.id))
            .filter((deliverableId) => Number.isFinite(deliverableId))
        const currentDeliverableSet = new Set(currentDeliverableIds)

        const addDeliverables = []
        const updateDeliverables = []

        const removeDeliverableIds = Array.from(originalDeliverablesById.entries())
            .filter(([, originalDeliverable]) =>
                canEditDeliverableRecord(originalDeliverable, currentStaffId, isProjectMaster)
            )
            .filter(([deliverableId]) => !currentDeliverableSet.has(deliverableId))
            .map(([deliverableId]) => deliverableId)

        deliverables.forEach((deliverable) => {
            if (!canEditDeliverableRecord(deliverable, currentStaffId, isProjectMaster)) return

            const deliverableId = Number(deliverable?.id)
            const nextDeliverable = buildDeliverablePayload(deliverable)

            if (deliverable?.id == null) {
                addDeliverables.push(nextDeliverable)
                return
            }

            const originalDeliverable = originalDeliverablesById.get(deliverableId)
            if (!originalDeliverable) return

            const changed =
                nextDeliverable.type !== (originalDeliverable?.deliverable_type ?? '') ||
                nextDeliverable.value !== Number(originalDeliverable?.target_value) ||
                nextDeliverable.overflow_allowed !== Boolean(originalDeliverable?.overflow_allowed)

            if (changed) {
                updateDeliverables.push({
                    id: deliverableId,
                    ...nextDeliverable,
                })
            }
        })

        if (addDeliverables.length > 0 || removeDeliverableIds.length > 0 || updateDeliverables.length > 0) {
            updateMemberDeliverables.push({
                staff_id: staffId,
                ...(addDeliverables.length > 0 ? { add_deliverables: addDeliverables } : {}),
                ...(removeDeliverableIds.length > 0 ? { remove_deliverable_ids: removeDeliverableIds } : {}),
                ...(updateDeliverables.length > 0 ? { update_deliverables: updateDeliverables } : {}),
            })
        }
    })

    const removeTeamMemberIds = Array.from(originalTeamByStaffId.keys()).filter((staffId) => !currentTeamSet.has(staffId))

    if (addTeamMembers.length > 0) payload.add_team_members = addTeamMembers
    if (removeTeamMemberIds.length > 0) payload.remove_team_member_ids = removeTeamMemberIds
    if (updateMemberDeliverables.length > 0) payload.update_member_deliverables = updateMemberDeliverables

    return payload
}

export default function EditProjectModal({
    open,
    onClose,
    projectId,
    onProjectUpdated,
}) {
    const qc = useQueryClient()

    const [step, setStep] = React.useState(1)

    const [projectName, setProjectName] = React.useState('')
    const [objective, setObjective] = React.useState('')
    const [background, setBackground] = React.useState('')
    const [link, setLink] = React.useState('')
    const [startDate, setStartDate] = React.useState('')
    const [endDate, setEndDate] = React.useState('')

    const [touched, setTouched] = React.useState({
        projectName: false,
        startDate: false,
        endDate: false,
    })

    const [errors, setErrors] = React.useState({
        projectName: '',
        startDate: '',
        endDate: '',
    })

    const [query, setQuery] = React.useState('')
    const [selected, setSelected] = React.useState([])
    const [projectManagers, setProjectManagers] = React.useState([])
    const [submitError, setSubmitError] = React.useState('')
    const [showDiscardModal, setShowDiscardModal] = React.useState(false)
    const [initialSnapshot, setInitialSnapshot] = React.useState(null)
    const searchInputRef = React.useRef(null)
    const { data: projRes, isLoading: projectLoading } = useGetProjectTrackerProjectById(projectId, {
        enabled: open && Boolean(projectId),
    })
    const project = projRes?.data?.project ?? null

    const { mutateAsync: updateProject, isLoading: updatingProject } = useUpdateProjectTrackerProject()

    const { data: myAccountData } = useMyAccount({ enabled: open })
    const currentUserStaff = React.useMemo(() => {
        const s = myAccountData?.data?.staff ?? null
        if (!s) return null
        return {
            id: s?.id ?? null,
            name: s?.name ?? '-',
            avatarUrl: getStaffAvatar(s),
        }
    }, [myAccountData])

    const currentStaffId = currentUserStaff?.id ?? null
    const projectMasterStaffId = project?.created_by ?? null

    const isProjectMaster = React.useMemo(() => {
        return currentStaffId != null && projectMasterStaffId != null && String(currentStaffId) === String(projectMasterStaffId)
    }, [currentStaffId, projectMasterStaffId])

    const projectMasterStaff = React.useMemo(() => {
        return resolveProjectMasterStaff(project)
    }, [project])

    const selectedIds = React.useMemo(() => new Set(selected.map((s) => String(s.id))), [selected])

    const [debouncedQuery, setDebouncedQuery] = React.useState('')
    React.useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query.trim()), 250)
        return () => clearTimeout(t)
    }, [query])

    const {
        data: staffRes,
        isLoading: staffLoading,
        isError: staffIsError,
        error: staffError,
    } = useGetProjectTrackerStaff({ search: debouncedQuery }, { enabled: open && debouncedQuery.length > 0 })

    const staffFlat = React.useMemo(() => {
        const staffByDept = staffRes?.data?.staff ?? {}

        const flat = Object.entries(staffByDept).flatMap(([deptName, arr]) => {
            if (!Array.isArray(arr)) return []

            return arr
                .filter((s) => !isHiddenProjectTrackerStaff(s))
                .map((s) => ({
                    id: String(s.id),
                    userUuid: s.user_uuid ?? null,
                    name: s.name ?? '-',
                    department: s.department ?? deptName ?? '-',
                    designation: s.designation ?? '-',
                    workEmail: s.work_email ?? null,
                    employmentStatus: s.employment_status ?? null,
                    avatarUrl: s.profile_image_url ?? null,

                    totalActiveProjects: Number(s.total_active_projects ?? 0),
                    activeProjects: Array.isArray(s.active_projects) ? s.active_projects : [],
                }))
        })

        return flat.filter((staff) => Number(staff?.employmentStatus) === 1)
    }, [staffRes])

    const searched = React.useMemo(() => {
        const q = debouncedQuery.trim().toLowerCase()
        if (!q) return []
        return staffFlat
            .filter((m) => m.name && m.name.toLowerCase().includes(q))
            .filter((m) => !selectedIds.has(m.id))
    }, [staffFlat, debouncedQuery, selectedIds])

    const hasHydratedRef = React.useRef(false)
    // React.useEffect(() => {
    //     if (!open) return
    //     if (!project) return

    //     setStep(1)
    //     setSubmitError('')

    //     setProjectName(project?.name ?? '')
    //     setObjective(project?.objective ?? '')
    //     setBackground(project?.background ?? '')
    //     setLink(toLinksText(project?.related_links ?? []))

    //     setStartDate(isoToDDMMYYYY(project?.start_date))
    //     setEndDate(isoToDDMMYYYY(project?.deadline))

    //     const allMembers = project?.members ?? []

    //     const mgrMembers = allMembers.filter((m) => String(m?.role || '').toLowerCase() === 'manager')

    //     const mgrStaffResolved = mgrMembers
    //         .map((m) => {
    //             if (m?.staff?.id) return m.staff
    //             const same = allMembers.find((x) => String(x?.staff_id) === String(m?.staff_id) && x?.staff?.id)
    //             return same?.staff ?? null
    //         })
    //         .filter(Boolean)

    //     const mgrMap = new Map()
    //     mgrStaffResolved.forEach((s) => mgrMap.set(String(s.id), s))
    //     setProjectManagers(Array.from(mgrMap.values()))

    //     const teamMembers = (allMembers ?? [])
    //         .filter((m) => String(m?.role || '').toLowerCase() === 'team_member')
    //         .map((m) => {
    //             const staff = m?.staff ?? null

    //             const deliverables = (m?.deliverables ?? []).map((d) => ({
    //                 _tmpId: `d-${d?.id ?? Math.random()}`,
    //                 id: d?.id ?? null,
    //                 type: d?.deliverable_type ?? '',
    //                 value: d?.target_value ?? '',
    //                 allowOverflow: Boolean(d?.overflow_allowed),
    //                 touched: { type: false, value: false },
    //                 isNew: false,
    //             }))

    //             return {
    //                 id: String(staff?.id ?? m?.staff_id),
    //                 name: staff?.name ?? '-',
    //                 department: staff?.department ?? '-',
    //                 designation: staff?.designation ?? '-',
    //                 avatarUrl: staff?.profile_image_url ?? null,
    //                 role: 'team_member',
    //                 isOpen: false,
    //                 deliverables,
    //             }
    //         })
    //         .filter((x) => x?.id)

    //     const tmMap = new Map()
    //     teamMembers.forEach((x) => tmMap.set(String(x.id), x))
    //     setSelected(Array.from(tmMap.values()))

    //     setQuery('')
    //     setDebouncedQuery('')

    //     setTouched({ projectName: false, startDate: false, endDate: false })
    //     setErrors({ projectName: '', startDate: '', endDate: '' })
    // }, [open, project])
    React.useEffect(() => {
        if (!open) {
            hasHydratedRef.current = false
            setInitialSnapshot(null)
            setShowDiscardModal(false)
            return
        }

        if (!project || hasHydratedRef.current) return

        hasHydratedRef.current = true

        const loadedProjectName = project?.name ?? ''
        const loadedObjective = project?.objective ?? ''
        const loadedBackground = project?.background ?? ''
        const loadedLink = toLinksText(project?.related_links ?? [])
        const loadedStartDate = isoToDDMMYYYY(project?.start_date)
        const loadedEndDate = isoToDDMMYYYY(project?.deadline)

        setStep(1)
        setSubmitError('')

        setProjectName(loadedProjectName)
        setObjective(loadedObjective)
        setBackground(loadedBackground)
        setLink(loadedLink)

        setStartDate(loadedStartDate)
        setEndDate(loadedEndDate)

        const allMembers = project?.members ?? []

        const mgrMembers = allMembers.filter((m) => String(m?.role || '').toLowerCase() === 'manager')

        const mgrStaffResolved = mgrMembers
            .map((m) => {
                if (m?.staff?.id) return m.staff
                const same = allMembers.find((x) => String(x?.staff_id) === String(m?.staff_id) && x?.staff?.id)
                return same?.staff ?? null
            })
            .filter(Boolean)

        const mgrMap = new Map()
        mgrStaffResolved.forEach((s) => mgrMap.set(String(s.id), s))
        const loadedProjectManagers = Array.from(mgrMap.values())
        setProjectManagers(loadedProjectManagers)

        const teamMembers = allMembers
            .filter((m) => String(m?.role || '').toLowerCase() === 'team_member')
            .map((m) => {
                const staff = m?.staff ?? null

                const deliverables = (m?.deliverables ?? []).map((d) => {
                    const creator = resolveDeliverableCreator(d, projectMasterStaff)

                    return {
                        _tmpId: `d-${d?.id ?? Math.random()}`,
                        id: d?.id ?? null,
                        type: d?.deliverable_type ?? '',
                        value: d?.target_value ?? '',
                        allowOverflow: Boolean(d?.overflow_allowed),
                        touched: { type: false, value: false },
                        isNew: false,
                        createdBy: d?.created_by ?? null,
                        creator,
                    }
                })

                return {
                    id: String(staff?.id ?? m?.staff_id),
                    name: staff?.name ?? '-',
                    department: staff?.department ?? '-',
                    designation: staff?.designation ?? '-',
                    avatarUrl: staff?.profile_image_url ?? null,
                    role: 'team_member',
                    isOpen: false,
                    deliverables,
                }
            })
            .filter((x) => x?.id)

        const tmMap = new Map()
        teamMembers.forEach((x) => tmMap.set(String(x.id), x))
        const loadedSelected = Array.from(tmMap.values())
        setSelected(loadedSelected)

        setQuery('')
        setDebouncedQuery('')
        setTouched({ projectName: false, startDate: false, endDate: false })
        setErrors({ projectName: '', startDate: '', endDate: '' })
        setShowDiscardModal(false)
        setInitialSnapshot(
            buildEditProjectSnapshot({
                projectName: loadedProjectName,
                objective: loadedObjective,
                background: loadedBackground,
                link: loadedLink,
                startDate: loadedStartDate,
                endDate: loadedEndDate,
                projectManagers: loadedProjectManagers,
                selected: loadedSelected,
            })
        )
    }, [open, project, projectMasterStaff])

    const validateStep1 = React.useCallback(() => {
        const next = { projectName: '', startDate: '', endDate: '' }

        if (!projectName.trim()) next.projectName = 'Please enter project name'

        if (!startDate.trim()) next.startDate = 'Please enter project start date'
        else if (!isValidDateDDMMYYYY(startDate)) next.startDate = 'Date must be in DD/MM/YYYY'

        if (!endDate.trim()) next.endDate = 'Please enter project end date'
        else if (!isValidDateDDMMYYYY(endDate)) next.endDate = 'Date must be in DD/MM/YYYY'

        if (isValidDateDDMMYYYY(startDate) && isValidDateDDMMYYYY(endDate)) {
            const [sd, sm, sy] = startDate.split('/').map(Number)
            const [ed, em, ey] = endDate.split('/').map(Number)
            const start = new Date(sy, sm - 1, sd).getTime()
            const end = new Date(ey, em - 1, ed).getTime()
            if (end < start) next.endDate = 'Project end date cannot be earlier than start date'
        }

        return next
    }, [projectName, startDate, endDate])

    function errorText(d, field) {
        if (!d.touched[field]) return ''
        if (field === 'type' && !d.type.trim()) return 'Please enter deliverable type'
        if (field === 'value' && !String(d.value).trim()) return 'Please enter deliverable value'
        return ''
    }

    function resetTransientUiState() {
        setStep(1)
        setSubmitError('')
        setShowDiscardModal(false)
    }

    function finalClose() {
        resetTransientUiState()
        onClose?.()
    }

    function requestClose(source, reason) {
        if (updatingProject) return
        if (reason === 'backdropClick') return

        if (!isDirty) {
            finalClose()
            return
        }

        if (reason === 'escapeKeyDown' || source === 'icon' || source === 'cancel') {
            setShowDiscardModal(true)
            return
        }

        setShowDiscardModal(true)
    }

    function addSelectedMember(m) {
        if (selectedIds.has(String(m.id))) return

        setSelected((prev) => [
            ...prev,
            {
                id: String(m.id),
                name: m.name,
                department: m.department,
                designation: m.designation,
                avatarUrl: m.avatarUrl,

                totalActiveProjects: m.totalActiveProjects ?? 0,
                activeProjects: Array.isArray(m.activeProjects) ? m.activeProjects : [],

                role: 'team_member',
                isOpen: false,
                deliverables: [],
            },
        ])

        // reset search + close dropdown
        setQuery('')
        setDebouncedQuery('')
        searchInputRef.current?.blur?.()
    }

    function removeMember(memberId) {
        setSelected((prev) => prev.filter((p) => String(p.id) !== String(memberId)))
    }

    function toggleMember(memberId) {
        setSelected((prev) => prev.map((p) => (String(p.id) === String(memberId) ? { ...p, isOpen: !p.isOpen } : p)))
    }

    function addDeliverable(memberId) {
        setSelected((prev) =>
            prev.map((p) =>
                String(p.id) === String(memberId)
                    ? { ...p, isOpen: true, deliverables: [...p.deliverables, newDeliverable(currentUserStaff)] }
                    : p
            )
        )
    }

    function removeDeliverable(memberId, deliverableTmpOrId) {
        setSelected((prev) =>
            prev.map((p) => {
                if (String(p.id) !== String(memberId)) return p
                return {
                    ...p,
                    deliverables: p.deliverables.filter(
                        (d) => String(d._tmpId) !== String(deliverableTmpOrId) && String(d.id) !== String(deliverableTmpOrId)
                    ),
                }
            })
        )
    }

    function updateDeliverable(memberId, deliverableTmpOrId, patch) {
        setSelected((prev) =>
            prev.map((p) => {
                if (String(p.id) !== String(memberId)) return p
                return {
                    ...p,
                    deliverables: p.deliverables.map((d) => {
                        const same = String(d._tmpId) === String(deliverableTmpOrId) || String(d.id) === String(deliverableTmpOrId)
                        return same ? { ...d, ...patch } : d
                    }),
                }
            })
        )
    }

    function markTouched(memberId, deliverableTmpOrId, field) {
        setSelected((prev) =>
            prev.map((p) => {
                if (String(p.id) !== String(memberId)) return p
                return {
                    ...p,
                    deliverables: p.deliverables.map((d) => {
                        const same = String(d._tmpId) === String(deliverableTmpOrId) || String(d.id) === String(deliverableTmpOrId)
                        if (!same) return d
                        return { ...d, touched: { ...d.touched, [field]: true } }
                    }),
                }
            })
        )
    }

    function hasStep2Errors() {
        return selected.some((m) =>
            m.deliverables.some((d) => {
                if (!canEditDeliverableRecord(d, currentStaffId, isProjectMaster)) return false
                return !d.type.trim() || !String(d.value).trim()
            })
        )
    }

    function touchAllDeliverables() {
        setSelected((prev) =>
            prev.map((m) => ({
                ...m,
                isOpen: true,
                deliverables: m.deliverables.map((d) => ({
                    ...d,
                    touched: canEditDeliverableRecord(d, currentStaffId, isProjectMaster)
                        ? { ...d.touched, type: true, value: true }
                        : d.touched,
                })),
            }))
        )
    }

    function handleNextStep() {
        setTouched((prev) => ({ ...prev, projectName: true, startDate: true, endDate: true }))

        const nextErrors = validateStep1()
        setErrors(nextErrors)

        const hasAnyError = Object.values(nextErrors).some(Boolean)
        if (hasAnyError) return

        setSubmitError('')
        setStep(2)
    }

    function handleManagersChange(nextManagers) {
        const mgrObjs = (nextManagers || []).filter((s) => s?.id != null)

        const map = new Map()
        mgrObjs.forEach((s) => map.set(String(s.id), s))

        if (isProjectMaster && projectMasterStaffId != null && !map.has(String(projectMasterStaffId))) {
            const projectMasterManager =
                projectManagers.find((s) => String(s?.id) === String(projectMasterStaffId)) ??
                toProjectManagerStaff(projectMasterStaff)

            if (projectMasterManager?.id != null) {
                map.set(String(projectMasterManager.id), projectMasterManager)
            }
        }

        const deduped = Array.from(map.values())

        setProjectManagers(deduped)
    }

    // async function handleSaveProject() {
    //     setSubmitError('')

    //     touchAllDeliverables()
    //     if (hasStep2Errors()) return

    //     const nextErrors = validateStep1()
    //     setErrors(nextErrors)
    //     const hasAnyError = Object.values(nextErrors).some(Boolean)
    //     if (hasAnyError) return

    //     if (!projectId) {
    //         setSubmitError('Missing project id.')
    //         return
    //     }

    //     const startISO = toISODateFromDDMMYYYY(startDate)
    //     const endISO = toISODateFromDDMMYYYY(endDate)

    //     if (!startISO) {
    //         setSubmitError('Start date is required and must be valid.')
    //         return
    //     }
    //     if (!endISO) {
    //         setSubmitError('Deadline is required and must be valid.')
    //         return
    //     }

    //     const pmIds = (projectManagers || [])
    //         .map((m) => Number(m?.id))
    //         .filter((v) => Number.isFinite(v))

    //     if (pmIds.length === 0) {
    //         setSubmitError('Please add at least 1 project manager.')
    //         return
    //     }

    //     if ((selected?.length ?? 0) === 0) {
    //         setSubmitError('Please add at least 1 team member.')
    //         return
    //     }

    //     const payload = {
    //         name: projectName.trim(),
    //         objective: objective?.trim() || null,
    //         background: background?.trim() || null,
    //         related_links: toRelatedLinks(link),
    //         start_date: startISO,
    //         deadline: endISO,
    //         project_managers: pmIds,
    //         team_members: selected.map((m) => ({
    //             staff_id: Number(m.id),
    //             deliverables: (m.deliverables || []).map((d) => {
    //                 const base = {
    //                     type: String(d.type || '').trim(),
    //                     value: Number(d.value),
    //                     overflow_allowed: Boolean(d.allowOverflow),
    //                 }
    //                 if (d?.id) return { id: d.id, ...base }
    //                 return base
    //             }),
    //         })),
    //     }

    //     try {
    //         const res = await updateProject({ projectId, payload })
    //         const apiProject = res?.data?.project

    //         await qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
    //         await qc.invalidateQueries([PROJECT_TRACKER_PROJECT_QK, projectId])

    //         onProjectUpdated?.(apiProject)
    //         closeModal()
    //     } catch (e) {
    //         const msg = e?.response?.data?.message || e?.message || 'Failed to update project. Please try again.'
    //         setSubmitError(msg)
    //     }
    // }
    async function handleSaveProject() {
        setSubmitError('')

        touchAllDeliverables()
        if (hasStep2Errors()) return

        const nextErrors = validateStep1()
        setErrors(nextErrors)
        const hasAnyError = Object.values(nextErrors).some(Boolean)
        if (hasAnyError) return

        if (!projectId) {
            setSubmitError('Missing project id.')
            return
        }

        const startISO = toISODateFromDDMMYYYY(startDate)
        const endISO = toISODateFromDDMMYYYY(endDate)

        if (!startISO) {
            setSubmitError('Start date is required and must be valid.')
            return
        }
        if (!endISO) {
            setSubmitError('Deadline is required and must be valid.')
            return
        }

        const pmIds = (projectManagers || [])
            .map((m) => Number(m?.id))
            .filter((v) => Number.isFinite(v))

        if (pmIds.length === 0) {
            setSubmitError('Please add at least 1 project manager.')
            return
        }

        if ((selected?.length ?? 0) === 0) {
            setSubmitError('Please add at least 1 team member.')
            return
        }

        const payload = buildProjectPatchPayload(
            project,
            {
                projectName,
                objective,
                background,
                link,
                startISO,
                endISO,
                projectManagers,
                selected,
            },
            {
                currentStaffId,
                isProjectMaster,
            }
        )

        try {
            const res = await updateProject({ projectId, payload })
            const apiProject = res?.data?.project

            finalClose() // close first

            onProjectUpdated?.(apiProject)

            qc.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            qc.invalidateQueries([PROJECT_TRACKER_PROJECT_QK, projectId])
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || 'Failed to update project. Please try again.'
            setSubmitError(msg)
        }
    }

    const modalShellSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(1100px, 92vw)',
        maxHeight: '88vh',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: 24,
    }

    function buildEditProjectSnapshot({
        projectName,
        objective,
        background,
        link,
        startDate,
        endDate,
        projectManagers,
        selected,
    }) {
        return {
            projectName: (projectName || '').trim(),
            objective: (objective || '').trim(),
            background: (background || '').trim(),
            link: (link || '').trim(),
            startDate: (startDate || '').trim(),
            endDate: (endDate || '').trim(),
            projectManagers: (projectManagers || [])
                .map((m) => Number(m?.id))
                .filter((id) => Number.isFinite(id))
                .sort((a, b) => a - b),
            selected: (selected || [])
                .map((member) => ({
                    id: String(member?.id ?? ''),
                    deliverables: (member?.deliverables || [])
                        .map((d) => ({
                            id: d?.id ?? null,
                            _tmpId: d?._tmpId ?? null,
                            type: String(d?.type || '').trim(),
                            value: String(d?.value ?? '').trim(),
                            allowOverflow: Boolean(d?.allowOverflow),
                        }))
                        .sort((a, b) =>
                            String(a.id ?? a._tmpId).localeCompare(String(b.id ?? b._tmpId))
                        ),
                }))
                .sort((a, b) => a.id.localeCompare(b.id)),
        }
    }

    const currentSnapshot = React.useMemo(
        () =>
            buildEditProjectSnapshot({
                projectName,
                objective,
                background,
                link,
                startDate,
                endDate,
                projectManagers,
                selected,
            }),
        [projectName, objective, background, link, startDate, endDate, projectManagers, selected]
    )

    const isDirty = React.useMemo(() => {
        if (!initialSnapshot) return false
        return JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot)
    }, [currentSnapshot, initialSnapshot])

    return (
        <>
        <Modal open={open} onClose={(event, reason) => requestClose('modal', reason)}>
            <Paper sx={modalShellSx}>
                <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: '#dbeafe', width: 45, height: 45 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M3.5 6.25V8H8.129C8.32785 7.99983 8.51848 7.92069 8.659 7.78L10.189 6.25L8.659 4.72C8.51848 4.57931 8.32785 4.50017 8.129 4.5H5.25C4.78587 4.5 4.34075 4.68437 4.01256 5.01256C3.68437 5.34075 3.5 5.78587 3.5 6.25ZM2 6.25C2 5.38805 2.34241 4.5614 2.9519 3.9519C3.5614 3.34241 4.38805 3 5.25 3H8.129C8.72539 3.00026 9.29728 3.23729 9.719 3.659L11.562 5.5H18.751C19.6128 5.50027 20.4392 5.84279 21.0485 6.45226C21.6577 7.06172 22 7.88822 22 8.75V12.81C21.5539 12.3811 21.0488 12.0181 20.5 11.732V8.75C20.5 8.28587 20.3156 7.84075 19.9874 7.51256C19.6592 7.18437 19.2141 7 18.75 7H11.56L9.72 8.841C9.51107 9.04993 9.26303 9.21566 8.99004 9.32874C8.71706 9.44181 8.42447 9.5 8.129 9.5H3.5V17.75C3.5 18.716 4.284 19.5 5.25 19.5H11.313C11.486 20.034 11.725 20.537 12.022 21H5.25C4.38805 21 3.5614 20.6576 2.9519 20.0481C2.34241 19.4386 2 18.612 2 17.75V6.25ZM23 17.5C23 16.0413 22.4205 14.6424 21.3891 13.6109C20.3576 12.5795 18.9587 12 17.5 12C16.0413 12 14.6424 12.5795 13.6109 13.6109C12.5795 14.6424 12 16.0413 12 17.5C12 18.9587 12.5795 20.3576 13.6109 21.3891C14.6424 22.4205 16.0413 23 17.5 23C18.9587 23 20.3576 22.4205 21.3891 21.3891C22.4205 20.3576 23 18.9587 23 17.5ZM18 18L18.001 20.503C18.001 20.6356 17.9483 20.7628 17.8546 20.8566C17.7608 20.9503 17.6336 21.003 17.501 21.003C17.3684 21.003 17.2412 20.9503 17.1474 20.8566C17.0537 20.7628 17.001 20.6356 17.001 20.503V18H14.496C14.3634 18 14.2362 17.9473 14.1424 17.8536C14.0487 17.7598 13.996 17.6326 13.996 17.5C13.996 17.3674 14.0487 17.2402 14.1424 17.1464C14.2362 17.0527 14.3634 17 14.496 17H17V14.5C17 14.3674 17.0527 14.2402 17.1464 14.1464C17.2402 14.0527 17.3674 14 17.5 14C17.6326 14 17.7598 14.0527 17.8536 14.1464C17.9473 14.2402 18 14.3674 18 14.5V17H20.497C20.6296 17 20.7568 17.0527 20.8506 17.1464C20.9443 17.2402 20.997 17.3674 20.997 17.5C20.997 17.6326 20.9443 17.7598 20.8506 17.8536C20.7568 17.9473 20.6296 18 20.497 18H18Z" fill="#2666BE" />
                            </svg>
                        </Avatar>
                        <Typography variant="h3" sx={{ fontWeight: 900 }}>
                            Edit Project
                        </Typography>
                    </Stack>

                    <IconButton onClick={() => requestClose('icon')}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                <Box sx={{ p: 3, maxHeight: 'calc(88vh - 150px)', overflowY: 'auto' }}>
                    {projectLoading ? (
                        <Typography sx={{ color: 'rgba(0,0,0,0.6)', fontWeight: 700 }}>Loading project...</Typography>
                    ) : step === 1 ? (
                        <>
                            <Typography variant="h4" sx={{ fontWeight: 500, mb: 1 }}>
                                Step 1 of 2: Project Details
                            </Typography>
                            <StepDots step={1} />

                            <Box sx={{ mt: 3 }}>
                                <AddManager
                                    value={projectManagers}
                                    onChange={handleManagersChange}
                                    autoAddSelf={false}
                                    disabled={!isProjectMaster}
                                    protectedStaffIds={projectMasterStaffId != null ? [projectMasterStaffId] : []}
                                />
                            </Box>

                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                        Project Name<span style={{ color: 'red' }}>*</span>
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter project name"
                                        value={projectName}
                                        onChange={(e) => {
                                            setProjectName(e.target.value)
                                            setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                        }}
                                        onBlur={() => {
                                            setTouched((prev) => ({ ...prev, projectName: true }))
                                            setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                        }}
                                        inputProps={{ maxLength: 45 }}
                                        error={touched.projectName && Boolean(errors.projectName)}
                                        helperText={touched.projectName && errors.projectName ? errors.projectName : `${projectName.length}/45`}
                                        FormHelperTextProps={{
                                            sx: {
                                                textAlign: touched.projectName && errors.projectName ? 'left' : 'right',
                                                mr: 0,
                                            },
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                        Project Objective
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        placeholder="Enter project objective"
                                        value={objective}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            if (countWords(value) <= 500) {
                                                setObjective(value)
                                            }
                                        }}
                                        helperText={`${countWords(objective)}/500 words`}
                                        FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                        Project Background
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        placeholder="Enter project background"
                                        value={background}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            if (countWords(value) <= 500) {
                                                setBackground(value)
                                            }
                                        }}
                                        helperText={`${countWords(background)}/500 words`}
                                        FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                        Link
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter a link (comma/newline separated allowed)"
                                        value={link}
                                        onChange={(e) => setLink(e.target.value)}
                                        inputProps={{ maxLength: 500 }}
                                    />
                                </Box>

                                <Box>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                                    Project Start<span style={{ color: 'red' }}>*</span>
                                                </Typography>

                                                <DateFieldWithIcon
                                                    value={startDate}
                                                    onChange={(v) => {
                                                        setStartDate(v)
                                                        setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                                    }}
                                                    onBlur={() => {
                                                        setTouched((prev) => ({ ...prev, startDate: true }))
                                                        setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                                    }}
                                                    error={touched.startDate && Boolean(errors.startDate)}
                                                    helperText={touched.startDate ? errors.startDate : ''}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
                                                    Project End<span style={{ color: 'red' }}>*</span>
                                                </Typography>

                                                <DateFieldWithIcon
                                                    value={endDate}
                                                    onChange={(v) => {
                                                        setEndDate(v)
                                                        setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                                    }}
                                                    onBlur={() => {
                                                        setTouched((prev) => ({ ...prev, endDate: true }))
                                                        setErrors((prev) => ({ ...prev, ...validateStep1() }))
                                                    }}
                                                    minDate={startDate}
                                                    error={touched.endDate && Boolean(errors.endDate)}
                                                    helperText={touched.endDate ? errors.endDate : ''}
                                                />
                                            </Grid>
                                        </Grid>
                                    </LocalizationProvider>
                                </Box>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, mb: 1 }}>Step 2 of 2: Team and Deliverable Management</Typography>
                            <StepDots step={2} />

                            {submitError ? (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor: 'rgba(239,68,68,0.08)',
                                        border: '1px solid rgba(239,68,68,0.35)',
                                    }}
                                >
                                    <Typography sx={{ color: '#ef4444', fontWeight: 800 }}>{submitError}</Typography>
                                </Box>
                            ) : null}

                            <TextField
                                fullWidth
                                sx={{ mt: 3 }}
                                placeholder="Search member name"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {query.trim() !== '' && (
                                <Box sx={{ mt: 2, border: '1px solid rgba(0,0,0,0.25)', borderRadius: 2, overflow: 'hidden' }}>
                                    {staffLoading ? (
                                        <Box sx={{ px: 2, py: 2, backgroundColor: 'white' }}>
                                            <Typography sx={{ color: 'rgba(0,0,0,0.6)', fontWeight: 700 }}>Loading members...</Typography>
                                        </Box>
                                    ) : staffIsError ? (
                                        <Box sx={{ px: 2, py: 2, backgroundColor: 'white' }}>
                                            <Typography sx={{ color: '#ef4444', fontWeight: 800 }}>Failed to load members</Typography>
                                            <Typography sx={{ color: 'rgba(0,0,0,0.6)', mt: 0.5 }}>
                                                {String(staffError?.message ?? 'Please try again.')}
                                            </Typography>
                                        </Box>
                                    ) : searched.length === 0 ? (
                                        <Box sx={{ px: 2, py: 2, backgroundColor: 'white' }}>
                                            <Typography sx={{ color: 'rgba(0,0,0,0.5)', fontWeight: 600 }}>No results found</Typography>
                                        </Box>
                                    ) : (
                                        searched.map((m, idx) => (
                                            <Box
                                                key={m.id}
                                                onClick={() => addSelectedMember(m)}
                                                sx={{
                                                    px: 2,
                                                    py: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    cursor: 'pointer',
                                                    borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.08)',
                                                    backgroundColor: 'white',
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <MemberAvatar name={m.name} src={m.avatarUrl} size={26} />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 900, fontSize: 18 }}>{m.name}</Typography>
                                                        <Typography sx={{ color: 'rgba(0,0,0,0.55)', fontWeight: 700, fontSize: 13 }}>
                                                            {m.designation} • {m.department}
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                                {/* <Chip
                                                    label={m.department}
                                                    sx={{
                                                        px: 1,
                                                        borderRadius: 999,
                                                        bgcolor: '#f3f4f6',
                                                        border: '1px solid rgba(0,0,0,0.12)',
                                                        fontWeight: 800,
                                                    }}
                                                /> */}
                                                <Tooltip
                                                    arrow={false}
                                                    placement="bottom-end"
                                                    enterDelay={100}
                                                    leaveDelay={100}
                                                    disableInteractive={false}
                                                    title={
                                                        <Box sx={{ p: 1.25, minWidth: 220, bgcolor: "#E5E5E5", borderRadius: '10px' }}>
                                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                    <g clipPath="url(#clip0_1496_11793)">
                                                                        <path d="M0 8C0 3.582 3.582 0 8 0C12.418 0 16 3.582 16 8C16 12.418 12.418 16 8 16C3.584 15.9947 0.00533333 12.416 0 8.00067V8ZM1.6 8C1.6 11.5347 4.46533 14.4 8 14.4C11.5347 14.4 14.4 11.5347 14.4 8C14.4 4.46533 11.5347 1.6 8 1.6C4.46733 1.604 1.604 4.46733 1.6 7.99933V8ZM4.26667 8C4.26667 7.00986 4.66 6.06027 5.36014 5.36014C6.06027 4.66 7.00986 4.26667 8 4.26667C8.99014 4.26667 9.93973 4.66 10.6399 5.36014C11.34 6.06027 11.7333 7.00986 11.7333 8C11.7333 8.99014 11.34 9.93973 10.6399 10.6399C9.93973 11.34 8.99014 11.7333 8 11.7333C7.00986 11.7333 6.06027 11.34 5.36014 10.6399C4.66 9.93973 4.26667 8.99014 4.26667 8Z" fill="#27AE60" />
                                                                    </g>
                                                                    <defs>
                                                                        <clipPath id="clip0_1496_11793">
                                                                            <rect width="16" height="16" fill="white" />
                                                                        </clipPath>
                                                                    </defs>
                                                                </svg>
                                                                <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                                                                    Active projects ({m.totalActiveProjects ?? 0}):
                                                                </Typography>
                                                            </Stack>

                                                            {Array.isArray(m.activeProjects) && m.activeProjects.length > 0 ? (
                                                                <Stack spacing={0.5}>
                                                                    {m.activeProjects.map((p) => (
                                                                        <Typography
                                                                            key={p.id ?? p.name}
                                                                            sx={{ fontSize: 15, fontWeight: 500, color: "rgba(0,0,0,0.85)" }}
                                                                        >
                                                                            {p?.name ?? "-"}
                                                                        </Typography>
                                                                    ))}
                                                                </Stack>
                                                            ) : (
                                                                <Typography sx={{ fontSize: 14, color: "rgba(0,0,0,0.55)" }}>
                                                                    No active projects
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: "#F7F9FA",
                                                                color: "black",
                                                                borderRadius: '10px',
                                                                p: 0,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        onClick={(e) => e.stopPropagation()} // ✅ same as Create: prevent selecting on pill click
                                                        sx={{
                                                            px: 1,
                                                            borderRadius: 999,
                                                            bgcolor: "#f3f4f6",
                                                            border: "1px solid rgba(0,0,0,0.12)",
                                                            padding: 1.1,
                                                            display: "flex",
                                                            width: "65px",
                                                            py: 0.75,
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <g clipPath="url(#clip0_1496_11788)">
                                                                <path d="M0 8C0 3.582 3.582 0 8 0C12.418 0 16 3.582 16 8C16 12.418 12.418 16 8 16C3.584 15.9947 0.00533333 12.416 0 8.00067V8ZM1.6 8C1.6 11.5347 4.46533 14.4 8 14.4C11.5347 14.4 14.4 11.5347 14.4 8C14.4 4.46533 11.5347 1.6 8 1.6C4.46733 1.604 1.604 4.46733 1.6 7.99933V8ZM4.26667 8C4.26667 7.00986 4.66 6.06027 5.36014 5.36014C6.06027 4.66 7.00986 4.26667 8 4.26667C8.99014 4.26667 9.93973 4.66 10.6399 5.36014C11.34 6.06027 11.7333 7.00986 11.7333 8C11.7333 8.99014 11.34 9.93973 10.6399 10.6399C9.93973 11.34 8.99014 11.7333 8 11.7333C7.00986 11.7333 6.06027 11.34 5.36014 10.6399C4.66 9.93973 4.26667 8.99014 4.26667 8Z" fill="#27AE60" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_1496_11788">
                                                                    <rect width="16" height="16" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>

                                                        <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                                                            {m.totalActiveProjects ?? 0}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            )}

                            <Box>
                                <Typography sx={{ mt: 3, color: 'rgba(0,0,0,0.45)', fontWeight: 600 }}>
                                    Assign deliverable types and values for each member.
                                </Typography>

                                <Stack spacing={2} sx={{ mt: 2 }}>
                                    {selected.map((m) => (
                                        <Box
                                            key={m.id}
                                            sx={{
                                                border: '1px solid rgba(0,0,0,0.18)',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                backgroundColor: 'white',
                                            }}
                                        >
                                            <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <MemberAvatar name={m.name} src={m.avatarUrl} size={28} />
                                                    <Box>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography sx={{ fontWeight: 900, fontSize: 20 }}>{m.name}</Typography>
                                                        </Stack>
                                                    </Box>
                                                </Stack>

                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Typography sx={{ color: 'rgba(0,0,0,0.45)', fontWeight: 700 }}>{m.deliverables.length} deliverable</Typography>

                                                    <IconButton onClick={() => removeMember(m.id)} sx={{ color: '#ef4444' }}>
                                                        <DeleteOutlineIcon />
                                                    </IconButton>

                                                    <IconButton onClick={() => toggleMember(m.id)} sx={{ color: 'black' }}>
                                                        <KeyboardArrowDownIcon
                                                            sx={{
                                                                transform: m.isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                transition: 'transform 120ms ease',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Stack>
                                            </Box>

                                            <Collapse in={m.isOpen} timeout={150} unmountOnExit>
                                                <Divider />
                                                <Box sx={{ p: 2, backgroundColor: 'rgba(245,248,251,1)' }}>
                                                    <Stack spacing={2}>
                                                        {m.deliverables.map((d) => {
                                                            const typeErr = errorText(d, 'type')
                                                            const valErr = errorText(d, 'value')
                                                            const key = d._tmpId || d.id
                                                            const editable = canEditDeliverableRecord(d, currentStaffId, isProjectMaster)
                                                            const creator = d.creator

                                                            return (
                                                                <Box
                                                                    key={key}
                                                                    sx={{
                                                                        p: 1.5,
                                                                        borderRadius: 2,
                                                                        bgcolor: editable ? 'transparent' : 'rgba(0,0,0,0.04)',
                                                                        opacity: editable ? 1 : 0.65,
                                                                    }}
                                                                >
                                                                    <Grid container spacing={2} alignItems="center">
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography sx={{ fontWeight: 800, mb: 1 }}>
                                                                                Deliverable Type<span style={{ color: 'red' }}>*</span>
                                                                            </Typography>
                                                                            <TextField
                                                                                fullWidth
                                                                                disabled={!editable}
                                                                                placeholder="Enter deliverable type"
                                                                                value={d.type}
                                                                                onChange={(e) => {
                                                                                    if (!editable) return
                                                                                    updateDeliverable(m.id, key, { type: e.target.value })
                                                                                    if (d.touched.type) markTouched(m.id, key, 'type')
                                                                                }}
                                                                                onBlur={() => editable && markTouched(m.id, key, 'type')}
                                                                                error={editable && Boolean(typeErr)}
                                                                                helperText={editable ? typeErr : ''}
                                                                            />
                                                                        </Grid>

                                                                        <Grid item xs={12} md={5}>
                                                                            <Typography sx={{ fontWeight: 800, mb: 1 }}>
                                                                                Deliverable Value<span style={{ color: 'red' }}>*</span>
                                                                            </Typography>
                                                                            <TextField
                                                                                fullWidth
                                                                                disabled={!editable}
                                                                                placeholder="Enter a number (e.g. 2, 3)"
                                                                                value={d.value}
                                                                                onChange={(e) => {
                                                                                    if (!editable) return
                                                                                    updateDeliverable(m.id, key, { value: e.target.value })
                                                                                    if (d.touched.value) markTouched(m.id, key, 'value')
                                                                                }}
                                                                                onBlur={() => editable && markTouched(m.id, key, 'value')}
                                                                                error={editable && Boolean(valErr)}
                                                                                helperText={editable ? valErr : ''}
                                                                            />
                                                                        </Grid>

                                                                        <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <IconButton
                                                                                disabled={!editable}
                                                                                onClick={() => editable && removeDeliverable(m.id, key)}
                                                                                sx={{ color: editable ? 'black' : 'rgba(0,0,0,0.25)' }}
                                                                            >
                                                                                —
                                                                            </IconButton>
                                                                        </Grid>
                                                                    </Grid>

                                                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                                                                        <Typography sx={{ fontWeight: 800 }}>Allow overflow?</Typography>
                                                                        <Switch
                                                                            disabled={!editable}
                                                                            checked={d.allowOverflow}
                                                                            onChange={(e) => {
                                                                                if (!editable) return
                                                                                updateDeliverable(m.id, key, { allowOverflow: e.target.checked })
                                                                            }}
                                                                        />
                                                                        {/* <Typography sx={{ fontWeight: 900, color: 'rgba(0,0,0,0.5)' }}>
                                                                            {d.allowOverflow ? 'YES' : 'NO'}
                                                                        </Typography> */}

                                                                        {creator ? (
                                                                            <Tooltip title={`Created by: ${creator.name}`} arrow>
                                                                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ml: 'auto' }}>
                                                                                    <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.4)', fontWeight: 600 }}>by</Typography>
                                                                                    <MemberAvatar name={creator.name} src={creator.avatarUrl} size={22} />
                                                                                </Stack>
                                                                            </Tooltip>
                                                                        ) : null}
                                                                    </Stack>
                                                                </Box>
                                                            )
                                                        })}

                                                        <Button startIcon={<AddIcon />} onClick={() => addDeliverable(m.id)} sx={{ textTransform: 'none', fontWeight: 900, width: 'fit-content' }}>
                                                            Add deliverable
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </>
                    )}
                </Box>

                <Box sx={{ px: 3, py: 2, background: '#f6efd9', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {step === 1 ? (
                        <>
                            <Button
                                variant="body1"
                                onClick={() => requestClose('cancel')}
                                sx={{
                                    borderRadius: 100,
                                    px: 4,
                                    py: 1.2,
                                    fontWeight: 500,
                                    border: '1px solid #F3BF3A',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNextStep}
                                sx={{
                                    borderRadius: 100,
                                    px: 5,
                                    py: 1.2,
                                    fontWeight: 500,
                                    bgcolor: '#F3BF3A',
                                    color: 'black',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: '#eab308', boxShadow: 'none' },
                                }}
                            >
                                Next
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="body1"
                                onClick={() => setStep(1)}
                                disabled={updatingProject}
                                sx={{
                                    borderRadius: 100,
                                    px: 4,
                                    py: 1.2,
                                    fontWeight: 500,
                                    border: '1px solid #F3BF3A',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveProject}
                                disabled={updatingProject}
                                sx={{
                                    borderRadius: 100,
                                    px: 5,
                                    py: 1.2,
                                    fontWeight: 500,
                                    bgcolor: '#F3BF3A',
                                    color: 'black',
                                    width: 'auto',
                                    minWidth: 'fit-content',
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: '#eab308', boxShadow: 'none' },
                                    '&.Mui-disabled': { opacity: 0.7, color: 'rgba(0,0,0,0.7)' },
                                }}
                            >
                                {updatingProject ? 'Saving...' : 'Save changes'}
                            </Button>
                        </>
                    )}
                </Box>
            </Paper>
        </Modal>
        <AdxInfoModal
            variant="error"
            open={showDiscardModal}
            onClose={() => setShowDiscardModal(false)}
            muiIcon="warning"
            iconColor="error.main"
            title="Discard changes?"
            subtitle="Any unsaved changes will be discarded"
            footerButtons
            firstBtnLabel="Discard"
            firstBtnHandle={finalClose}
            secondBtnLabel="Continue editing"
            secondBtnHandle={() => setShowDiscardModal(false)}
        />
        </>
    )
}
